import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Inventory2 as InventoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  TrendingDown as DeficitIcon,
} from "@mui/icons-material";
import { CoverageBadge } from "../components/Distribution/CoverageBadge";
import { StoreCoverageDialog } from "../components/Distribution/StoreCoverageDialog";
import { WidgetHomeCompras } from "../components/Distribution/WidgetHomeCompras";
import {
  useDistributionBacklog,
  usePendingDenials,
} from "../hooks/useDistributionCoverage";
import { usePageTitle } from "../context/PageTitleContext";

const TOP_BACKLOG_TO_SHOW = 30;

/**
 * Dashboard de distribución para el módulo de compras (B1.14 + B1.15).
 *
 * Muestra:
 *   - Widget home con cobertura agregada del sistema, top tiendas con déficit
 *     y top productos atascados (refresca cada 2 min).
 *   - Tabla de productos con denegaciones/atascos pendientes (backlog real).
 *   - Por cada producto: CoverageBadge con tooltip + click → dialog con desglose
 *     por tienda.
 *
 * Es una página NUEVA y aislada — no modifica ForecastPage existente (1917L
 * legacy). El CoverageBadge y StoreCoverageDialog son reutilizables y se
 * pueden integrar después en ForecastProductRow cuando haya una iteración
 * dedicada al refactor de esa página.
 */
export function DistribucionDashboardPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const [search, setSearch] = useState("");
  const [dialogProduct, setDialogProduct] = useState<{
    id: string;
    name?: string;
  } | null>(null);

  const { data: backlog, isLoading: backlogLoading } = useDistributionBacklog();
  const { data: pendingDenials } = usePendingDenials();

  useEffect(() => {
    setTitle("Distribución — Cobertura por tienda");
    return () => setTitle("");
  }, [setTitle]);

  const filteredBacklog = (backlog ?? []).filter((item) => {
    if (!search.trim()) return true;
    return item.unified_product_id
      .toLowerCase()
      .includes(search.toLowerCase());
  });

  const denialsByProduct = new Map<string, number>(
    (pendingDenials ?? []).map((d) => [
      d.unified_product_id,
      d.total_qty_denied,
    ]),
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <DeficitIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Distribución — Cobertura por tienda
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined">
          Esta vista muestra la cobertura calculada por el módulo de
          distribución (Bloque 1). El click sobre un producto abre el desglose
          por tienda con razón del déficit, ROP y stock actual.
        </Alert>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <WidgetHomeCompras
            onProductClick={(productId) =>
              setDialogProduct({ id: productId })
            }
          />

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              minHeight: 280,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Buscar producto
            </Typography>
            <TextField
              size="small"
              placeholder="Buscar por unified_product_id..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch("")}
                      aria-label="Limpiar búsqueda"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Mostrando {Math.min(filteredBacklog.length, TOP_BACKLOG_TO_SHOW)}
              {" de "}
              {filteredBacklog.length} productos en backlog.
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Backlog ordenado por antigüedad (denial más viejo primero).
            </Typography>
          </Paper>
        </Box>

        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Productos en backlog de distribución
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Productos con denegaciones pendientes que no se han satisfecho.
              Cada cobertura se calcula al vuelo con el cálculo desagregado
              por tienda.
            </Typography>
          </Box>

          {backlogLoading && (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}

          {!backlogLoading && filteredBacklog.length === 0 && (
            <Alert severity="success" sx={{ m: 2 }}>
              No hay productos en backlog que coincidan. Si esperabas ver
              elementos, verifica que el script de migración del backlog
              histórico haya corrido en staging.
            </Alert>
          )}

          {!backlogLoading && filteredBacklog.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Denegaciones</TableCell>
                    <TableCell align="right">Cantidad pendiente</TableCell>
                    <TableCell align="right">Tiendas afectadas</TableCell>
                    <TableCell align="right">Antigüedad</TableCell>
                    <TableCell align="center">Cobertura</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBacklog
                    .slice(0, TOP_BACKLOG_TO_SHOW)
                    .map((item) => {
                      const days = Math.floor(
                        (Date.now() -
                          new Date(item.oldest_denial_date).getTime()) /
                          (1000 * 60 * 60 * 24),
                      );
                      const totalDenied =
                        denialsByProduct.get(item.unified_product_id) ??
                        item.pending_denials_qty;
                      return (
                        <TableRow key={item.unified_product_id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <InventoryIcon
                                fontSize="small"
                                color="action"
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: "monospace" }}
                              >
                                {item.unified_product_id}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {item.pending_denials_count}
                          </TableCell>
                          <TableCell align="right">
                            {totalDenied.toFixed(0)} u
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${item.affected_stores.length}`}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={new Date(
                                item.oldest_denial_date,
                              ).toLocaleString()}
                            >
                              <Chip
                                size="small"
                                label={`${days}d`}
                                color={days > 30 ? "error" : "default"}
                                variant="outlined"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <CoverageBadge
                              unifiedProductId={item.unified_product_id}
                              onClickDetail={(pid) =>
                                setDialogProduct({ id: pid })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <StoreCoverageDialog
        unifiedProductId={dialogProduct?.id ?? null}
        {...(dialogProduct?.name ? { productName: dialogProduct.name } : {})}
        open={Boolean(dialogProduct)}
        onClose={() => setDialogProduct(null)}
      />
    </Box>
  );
}
